import { selectors } from './constants';

/**
 * Returns true if the given step terminates the wizard and the user cannot go forward.
 * @param {*} step 
 * @returns {Boolean}
 */
function terminatingStep(step) {
  const {
    actionName,
    conditions,
    panelType
  } = step.dataset;

  if (actionName === 'redirect' && isEmpty(conditions)) {
    // redirects cannot proceed
    return true;
  }

  if (!actionName && panelType !== 'form_wizard/panel_interstitial' && !step.querySelector([selectors.nextButton, ...selectors.fields].join())) {
    // content panels with no buttons or form controls cannot proceed
    return true;
  }
}

/**
 * Returns true if the value is blank or an empty JSON object
 */
const isEmpty = (value) => !value || ['{}', '[]'].includes(value);

/**
 * Returns true if the step cannot continue to the following step because it forcefully
 * branches off somewhere else in every condition.
 * @returns {Boolean}
 */
function terminatingBranchingStep(step) {
  return isEmpty(step.dataset.conditions) || step.dataset.stepNameElse;
}

function processGoToAction(step, steps, currentBranch, property = 'stepNameTo') {
  const stepNameTo = step.dataset[property];

  if(!stepNameTo) { return []; }

  // catch potential recursion exceptions
  if(currentBranch.length > 30) { throw new RangeError('Potential infinite recursion'); }

  // process the go-to action branch and add it to the total found branches
  const branchIndex = steps.findIndex(dest => dest.dataset.id === stepNameTo);
  return getSubBranches(steps, branchIndex, currentBranch);
}

function getBranchesRecursive(steps, currentBranch) {
  const branches = [];

  for(const step of steps) {
    currentBranch.push(step);

    if(step.dataset.actionName === 'go-to') {
      branches.push(...processGoToAction(step, steps, currentBranch));
      branches.push(...processGoToAction(step, steps, currentBranch, 'stepNameElse'));
    
      if (terminatingBranchingStep(step)) {
        // if go-to action is forced (not conditional), or we have an else condition, quit the loop
        // and do not add the current stack to the list of branches
        return branches;
      }
    }

    // if for any reason we cannot proceed to the next step, then break out here
    if (terminatingStep(step)) { break; }
  }

  branches.push(currentBranch);
  return branches;
}

function getSubBranches(steps, branchIndex, currentBranch) {
  if(branchIndex === -1) { return []; }
  return getBranchesRecursive(steps.slice(branchIndex), [...currentBranch]);
}

/**
 * Given an array of steps, parses them to extract all potential branches.
 * @param {Array} steps - The step fields of the form
 * @returns {Array[]} An array of arrays of steps, being all the different branches.
 */
export default function getBranches(steps) {
  try {
    return getBranchesRecursive(Array.from(steps), []);
  } catch(err) {
    if(err instanceof RangeError) { return []; }
    throw err;
  }
}
